import type {
   ApiEnvelope,
   DocumentExport,
   DocumentFacet,
   DocumentFilter,
   DocumentGetResponse,
   DocumentListResponse,
   DocumentSchemaField,
   DocumentSearchResponse,
   DocumentSort,
   MeasureRead,
} from '@/types/measure-document-types';
import { useApiFetch } from '@/services/api-client';

export interface MeasureDocumentAdapter {
   search: (params: {
      keyword?: string;
      filters?: DocumentFilter[];
      sort?: DocumentSort;
      page?: number;
      per_page?: number;
      /** Scope results to a single project's documents (e.g. a ProjectFacility). */
      project_id?: string;
   }) => Promise<DocumentSearchResponse>;

   list: (params: {
      filters?: DocumentFilter[];
      sort?: DocumentSort;
      page?: number;
      per_page?: number;
      measure_ids?: number[];
   }) => Promise<DocumentListResponse>;

   /** Bulk-fetch full MeasureReads for the given measure primary keys. */
   listByMeasureIds: (measureIds: number[]) => Promise<MeasureRead[]>;

   /**
    * Translate document identifiers to `Measure.id`, keyed by the value sent.
    *
    * Every other route here is keyed by the measure id, but callers still hold
    * NMED docids — a Luma citation, a `ProjectDocument` row written under the
    * older convention. The backend accepts either convention per value and omits
    * anything it cannot resolve for this caller, so a missing key means "no
    * document you can reach", not an error.
    */
   resolveMeasureIds: (documentIds: string[]) => Promise<Record<string, number>>;

   getById: (id: number) => Promise<MeasureRead>;

   /**
    * Export all search results to CSV and return a presigned S3 download URL.
    * The backend paginates internally (500/page, cap 10,000 rows).
    */
   exportCsv: (params: {
      keyword?: string;
      filters?: DocumentFilter[];
      sort?: DocumentSort | null;
      fields?: string[];
      /** Scope the export to one project's documents, mirroring `search`. */
      project_id?: string;
      /** Explicit document allowlist; intersects with `project_id`. */
      measure_ids?: number[];
   }) => Promise<DocumentExport>;

   /**
    * Fetch all available values for a single facet field — used by the Power Search UI.
    *
    * POWER SEARCH (not yet implemented in the UI):
    *   POST /backend/api/v1/measure-documents/search-facets
    *   Body: { keyword, filters, field: "GROUP.CODE", per_page }
    *   Returns: DocumentFacet with up to `per_page` (max 1000) buckets for the field.
    *
    * Intended use: when a user opens a facet field in the power-search panel and wants to
    * browse ALL available values, not just the top-10 returned in the main search response.
    * The UI should open a modal/flyout, call this method, and render a searchable list of values.
    */
   searchFacets: (params: {
      keyword?: string;
      filters?: DocumentFilter[];
      field: string;
      per_page?: number;
   }) => Promise<DocumentFacet>;

   /** Return up to 5 autocomplete suggestions for a partial search query. */
   suggest: (params: { keyword: string }) => Promise<string[]>;

   /**
    * Every document metadata field that can be filtered or sorted on.
    * Tenant-independent and stable, so callers cache it for the session.
    */
   schema: () => Promise<DocumentSchemaField[]>;
}

export function createMeasureDocumentAdapter(): MeasureDocumentAdapter {
   return {
      search({ keyword = '', filters = [], sort, page = 1, per_page = 10, project_id } = {}) {
         return useApiFetch<DocumentSearchResponse>('/measure-documents/search', {
            method: 'POST',
            body: { keyword, filters, sort: sort ?? null, page, per_page, project_id: project_id ?? null },
         });
      },

      list({ filters = [], sort, page = 1, per_page = 10, measure_ids } = {}) {
         return useApiFetch<DocumentListResponse>('/measure-documents/list', {
            method: 'POST',
            body: { filters, sort: sort ?? null, page, per_page, measure_ids: measure_ids ?? null },
         });
      },

      async listByMeasureIds(measureIds: number[]): Promise<MeasureRead[]> {
         if (measureIds.length === 0)
            return [];
         const response = await useApiFetch<DocumentListResponse>('/measure-documents/list', {
            method: 'POST',
            body: {
               filters: [],
               sort: null,
               page: 1,
               per_page: Math.min(measureIds.length, 500),
               measure_ids: measureIds,
            },
         });
         return response.data;
      },

      async resolveMeasureIds(documentIds: string[]): Promise<Record<string, number>> {
         if (documentIds.length === 0)
            return {};
         const response = await useApiFetch<ApiEnvelope<Record<string, number>>>(
            '/measure-documents/resolve-ids',
            {
               method: 'POST',
               body: { document_ids: documentIds },
            },
         );
         return response.data ?? {};
      },

      async getById(id: number): Promise<MeasureRead> {
         const httpRequest = await useApiFetch<DocumentGetResponse>(`/measure-documents/${id}`);
         return httpRequest.data;
      },

      // The four routes below return the `{ data, meta }` envelope. Unwrapping it
      // here keeps `meta` — which none of these callers use — out of the app and
      // leaves each method's return type the thing it says it returns.
      async exportCsv({ keyword = '', filters = [], sort = null, fields = [], project_id, measure_ids } = {}) {
         const response = await useApiFetch<ApiEnvelope<DocumentExport>>('/measure-documents/search/export-csv', {
            method: 'POST',
            body: {
               keyword,
               filters,
               sort,
               fields,
               project_id: project_id ?? null,
               measure_ids: measure_ids ?? null,
            },
            retry: 0, // CORS/network failures are not recoverable — fail fast
         });
         return response.data;
      },

      async searchFacets({ keyword = '', filters = [], field, per_page = 100 }: { keyword?: string; filters?: DocumentFilter[]; field: string; per_page?: number }) {
         const response = await useApiFetch<ApiEnvelope<DocumentFacet>>('/measure-documents/search-facets', {
            method: 'POST',
            body: { keyword, filters, field, per_page },
         });
         return response.data;
      },

      async suggest({ keyword }) {
         const response = await useApiFetch<ApiEnvelope<string[]>>('/measure-documents/suggest', {
            method: 'POST',
            body: { keyword },
         });
         return response.data;
      },

      async schema() {
         const response = await useApiFetch<ApiEnvelope<DocumentSchemaField[]>>('/measure-documents/schema');
         return response.data;
      },
   };
}
